import { VIEWER_EVENTS, POSITIONING_CONFIG } from "../config/constants.js";
import { Logger } from "../utils/Logger.js";

/**
 * Service for managing viewer events
 */
export class EventHandler {
	constructor(viewer) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("EventHandler");
		this.listeners = new Map();
		this.throttleTimers = new Map();
	}

	/**
	 * Initialize event handlers
	 */
	initialize() {
		this.logger.info("Initializing event handlers");
	}

	/**
	 * Add event listener with optional throttling
	 * @param {string} eventType - Event type
	 * @param {Function} handler - Event handler
	 * @param {Object} options - Options {throttle: number}
	 */
	addEventListener(eventType, handler, options = {}) {
		const { throttle } = options;

		let wrappedHandler = handler;

		if (throttle && throttle > 0) {
			wrappedHandler = this.createThrottledHandler(
				handler,
				throttle,
				eventType
			);
		}

		// Store original handler for removal
		if (!this.listeners.has(eventType)) {
			this.listeners.set(eventType, new Map());
		}
		this.listeners.get(eventType).set(handler, wrappedHandler);

		// Add to viewer
		this.viewer.addEventListener(eventType, wrappedHandler);
	}

	/**
	 * Remove event listener
	 * @param {string} eventType - Event type
	 * @param {Function} handler - Original handler function
	 */
	removeEventListener(eventType, handler) {
		const eventListeners = this.listeners.get(eventType);
		if (eventListeners && eventListeners.has(handler)) {
			const wrappedHandler = eventListeners.get(handler);
			this.viewer.removeEventListener(eventType, wrappedHandler);
			eventListeners.delete(handler);

			this.logger.debug(`Removed event listener for ${eventType}`);
		}
	}

	/**
	 * Create throttled event handler
	 * @param {Function} handler - Original handler
	 * @param {number} delay - Throttle delay in ms
	 * @param {string} eventType - Event type for logging
	 * @returns {Function} Throttled handler
	 */
	createThrottledHandler(handler, delay, eventType) {
		return (...args) => {
			const timerId = `${eventType}_throttle`;

			if (this.throttleTimers.has(timerId)) {
				return; // Still throttling
			}

			// Execute handler
			handler(...args);

			// Set throttle timer
			const timer = setTimeout(() => {
				this.throttleTimers.delete(timerId);
			}, delay);

			this.throttleTimers.set(timerId, timer);
		};
	}

	/**
	 * Add selection change listener
	 * @param {Function} handler - Handler function
	 */
	onSelectionChanged(handler) {
		this.addEventListener(VIEWER_EVENTS.SELECTION_CHANGED, handler);
	}

	/**
	 * Add camera change listener with appropriate throttling
	 * @param {Function} handler - Handler function
	 * @param {boolean} is2D - Whether this is for 2D view
	 */
	onCameraChanged(handler, is2D = false) {
		const throttle = is2D
			? POSITIONING_CONFIG.CAMERA_CHANGE_THROTTLE_2D
			: POSITIONING_CONFIG.CAMERA_CHANGE_THROTTLE_3D;

		this.addEventListener(VIEWER_EVENTS.CAMERA_CHANGE, handler, { throttle });
	}

	/**
	 * Add explode change listener
	 * @param {Function} handler - Handler function
	 */
	onExplodeChanged(handler) {
		this.addEventListener(VIEWER_EVENTS.EXPLODE_CHANGE, handler);
	}

	/**
	 * Add isolate event listener
	 * @param {Function} handler - Handler function
	 */
	onIsolateChanged(handler) {
		this.addEventListener(VIEWER_EVENTS.ISOLATE, handler);
	}

	/**
	 * Clean up all event listeners and timers
	 */
	destroy() {
		// Clear all throttle timers
		for (const timer of this.throttleTimers.values()) {
			clearTimeout(timer);
		}
		this.throttleTimers.clear();

		// Remove all event listeners
		for (const [eventType, eventListeners] of this.listeners) {
			for (const [originalHandler, wrappedHandler] of eventListeners) {
				this.viewer.removeEventListener(eventType, wrappedHandler);
			}
		}
		this.listeners.clear();
	}

	/**
	 * Get event listener statistics
	 * @returns {Object} Statistics
	 */
	getStats() {
		const stats = {
			totalEvents: 0,
			eventTypes: [],
			activeThrottles: this.throttleTimers.size,
		};

		for (const [eventType, handlers] of this.listeners) {
			stats.eventTypes.push({
				type: eventType,
				handlerCount: handlers.size,
			});
			stats.totalEvents += handlers.size;
		}

		return stats;
	}
}
